import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import settingsLanguage from "../../language/settingsLanguage";
import LogoutSection from "./SettingsSections/LogoutSection";
import { authAPI } from "../../services/api";

function Settings() {
  const { language, setLanguage } = useLanguage();
  const text = settingsLanguage[language];
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authAPI.getCurrentUser();
  const isAdmin = currentUser && (currentUser.userId === 1 || currentUser.userName?.toLowerCase() === "admin");
  const isEmployee = currentUser?.role === "Employee";
  const permissions = currentUser?.permissions;

  const hasPermission = (code) => {
    if (isAdmin) return true;
    if (!permissions || permissions.length === 0) return false;
    return permissions.includes(code);
  };

  const [openGroup, setOpenGroup] = useState(() => {
    return location.state?.openSection || "general";
  });

  useEffect(() => {
    if (location.state?.openSection) {
      setOpenGroup(location.state.openSection);
    }
  }, [location.state?.openSection]);

  const toggleGroup = (group) => {
    setOpenGroup((prev) => (prev === group ? null : group));
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    document.cookie = `appLanguage=${newLang}; max-age=${365 * 24 * 60 * 60}; path=/`;
  };

  const navigateTo = (path, section) => {
    navigate(path, { state: { openSection: section } });
  };

  const SubCard = ({ icon, title, onClick, section }) => (
    <button
      type="button"
      onClick={() => {
        if (section) {
          navigateTo(onClick, section);
        } else {
          navigate(onClick);
        }
      }}
      className="settings-sub-card"
    >
      <div className="settings-sub-card-icon">
        <i className={`fas ${icon}`} />
      </div>
      <span className="settings-sub-card-title">{title}</span>
    </button>
  );

  const GroupHeader = ({ icon, title, groupKey }) => (
    <button
      type="button"
      onClick={() => toggleGroup(groupKey)}
      className="settings-group-header-btn"
    >
      <div className="settings-group-header-left">
        <div className="settings-group-icon">
          <i className={`fas ${icon}`} />
        </div>
        <h2 className="settings-group-title">{title}</h2>
      </div>
      <div className={`settings-group-chevron ${openGroup === groupKey ? "open" : ""}`}>
        <i className="fas fa-chevron-up" />
      </div>
    </button>
  );

  return (
    <div className="settings-hub">
      <header className="settings-hub-header">
        <div className="settings-hub-header-content">
          <div className="settings-hub-icon-wrapper">
            <i className="fas fa-ellipsis-h" />
          </div>
          <div>
            <h1 className="settings-hub-title">{text.pageTitle}</h1>
            <p className="settings-hub-subtitle">{text.pageSubtitle}</p>
          </div>
        </div>
        <LogoutSection text={text} header />
      </header>

      <main className="settings-hub-content">
        {/* General Settings */}
        <div className="settings-group-wrapper">
          <GroupHeader icon="fa-sliders-h" title={text.generalSettings} groupKey="general" />
          <div className={`settings-group-body ${openGroup === "general" ? "open" : ""}`}>
            <div className="settings-card-grid">
              <div className="settings-sub-card settings-sub-card-language">
                <div className="settings-sub-card-icon">
                  <i className="fas fa-language" />
                </div>
                <span className="settings-sub-card-title">{text.languageSectionTitle}</span>
                <select
                  value={language}
                  onChange={handleLanguageChange}
                  className="settings-lang-dropdown"
                >
                  <option value="en">{text.languageEnglish}</option>
                  <option value="mr">{text.languageMarathi}</option>
                </select>
              </div>
              {!currentUser?.role?.startsWith("Employee") && (
                <SubCard
                  icon="fa-user-circle"
                  title={text.profileSettings}
                  onClick="/settings/profile"
                  section="general"
                />
              )}
            </div>
          </div>
        </div>

        {/* Customer Management */}
        {hasPermission("SETTINGS.CUSTOMER") && (
        <div className="settings-group-wrapper">
          <GroupHeader icon="fa-users" title={text.customerManagementGroup} groupKey="customer" />
          <div className={`settings-group-body ${openGroup === "customer" ? "open" : ""}`}>
            <div className="settings-card-grid">
              <SubCard
                icon="fa-user-plus"
                title={text.addCustomer}
                onClick="/settings/customer/add"
                section="customer"
              />
              <SubCard
                icon="fa-user-edit"
                title={text.editDeleteCustomer}
                onClick="/settings/customer/list"
                section="customer"
              />
              <SubCard
                icon="fa-calendar-alt"
                title={text.customerLeave}
                onClick="/settings/customer/leaves"
                section="customer"
              />
            </div>
          </div>
        </div>
        )}

        {/* Master Management */}
        {hasPermission("SETTINGS.MASTER") && (
        <div className="settings-group-wrapper">
          <GroupHeader icon="fa-database" title={text.masterManagement} groupKey="master" />
          <div className={`settings-group-body ${openGroup === "master" ? "open" : ""}`}>
            <div className="settings-card-grid">
              <SubCard
                icon="fa-store"
                title={text.dairyInformation}
                onClick="/settings/dairy-info"
                section="master"
              />
              <SubCard
                icon="fa-rupee-sign"
                title={text.milkRateMaster}
                onClick="/settings/milk-rate"
                section="master"
              />
              <SubCard
                icon="fa-hand-holding-usd"
                title={text.allowanceMaster}
                onClick="/settings/allowance"
                section="master"
              />
            </div>
          </div>
        </div>
        )}

        {/* Manage Sales & Expenses */}
        {hasPermission("SETTINGS.SALES") && (
        <div className="settings-group-wrapper">
          <GroupHeader icon="fa-chart-line" title={text.salesExpensesGroup} groupKey="salesExpenses" />
          <div className={`settings-group-body ${openGroup === "salesExpenses" ? "open" : ""}`}>
            <div className="settings-card-grid">
              <SubCard
                icon="fa-wallet"
                title={text.expensesMaster}
                onClick="/settings/expenses"
                section="salesExpenses"
              />
              <SubCard
                icon="fa-tags"
                title={text.salesMaster}
                onClick="/settings/sales"
                section="salesExpenses"
              />
            </div>
          </div>
        </div>
        )}

        {/* Employee Management */}
        {hasPermission("SETTINGS.EMPLOYEE") && (
        <div className="settings-group-wrapper">
          <GroupHeader icon="fa-user-tie" title={text.employeeManagement || "Employee Management"} groupKey="employee" />
          <div className={`settings-group-body ${openGroup === "employee" ? "open" : ""}`}>
            <div className="settings-card-grid">
              <SubCard
                icon="fa-user-tie"
                title={text.employeeManagement || "Manage Employees"}
                onClick="/settings/employee"
                section="employee"
              />
            </div>
          </div>
        </div>
        )}

        {/* User Management (Admin Only) */}
        {isAdmin && (
          <div className="settings-group-wrapper">
            <GroupHeader icon="fa-user-shield" title={text.userManagementGroup} groupKey="users" />
            <div className={`settings-group-body ${openGroup === "users" ? "open" : ""}`}>
              <div className="settings-card-grid">
                <SubCard
                  icon="fa-users-cog"
                  title={text.userManagementTitle}
                  onClick="/settings/users"
                  section="users"
                />
              </div>
            </div>
          </div>
        )}

        {/* Role Management (Admin Only) */}
        {isAdmin && (
          <div className="settings-group-wrapper">
            <GroupHeader icon="fa-shield-alt" title="Role Management" groupKey="roles" />
            <div className={`settings-group-body ${openGroup === "roles" ? "open" : ""}`}>
              <div className="settings-card-grid">
                <SubCard
                  icon="fa-shield-alt"
                  title="Manage Permissions"
                  onClick="/admin/roles"
                  section="roles"
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Settings;
